// Secondary "Select samples" panel that slides over the cart drawer.
// Each row has a SELECT / SELECTED toggle button; toggling adds/removes the
// sample variant via the cart AJAX API (free, tagged _sample=true), then
// re-renders the cart drawer section — button states, the header count and the
// bottom "X / N samples selected" bar all come back server-rendered.
if (typeof SampleDrawer !== "function") {
  const SAMPLE_SECTIONS = ["cart-drawer", "cart-icon-bubble"];

  class SampleDrawer extends HTMLElement {
    constructor() {
      super();
      this.max = parseInt(this.getAttribute("data-max"), 10) || 2;
      this.errorEl = this.querySelector(".sample-drawer__error");
      this.panel = this.querySelector(".sample-drawer__panel");

      this.addEventListener("click", (event) => {
        if (event.target.closest("[data-sample-close]")) {
          event.preventDefault();
          this.close();
          return;
        }

        const toggle = event.target.closest(".sample-item__toggle");
        if (toggle && !toggle.disabled) this.onToggle(toggle);
      });

      this.addEventListener("keyup", (event) => {
        if (event.code === "Escape") this.close();
      });

      // Re-open automatically after a cart re-render replaced this element.
      if (window.__sampleDrawerReopen) {
        window.__sampleDrawerReopen = false;
        this.open();
      }
    }

    open(triggeredBy) {
      this.activeElement = triggeredBy || document.activeElement;
      this.classList.add("is-open");
      this.clearError();
    }

    close() {
      this.classList.remove("is-open");
      if (this.activeElement && typeof this.activeElement.focus === "function") {
        this.activeElement.focus();
      }
    }

    get selectedCount() {
      return this.querySelectorAll('.sample-item__toggle[data-selected="true"]')
        .length;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }

    setBusy(isBusy) {
      this.classList.toggle("is-busy", isBusy);
    }

    // Theme loading idiom: hide the label, unhide the loading-spinner snippet.
    setLoading(toggle, isLoading) {
      if (!toggle) return;
      toggle.classList.toggle("loading", isLoading);
      const spinner = toggle.querySelector(".loading__spinner");
      if (spinner) spinner.classList.toggle("hidden", !isLoading);
    }

    onToggle(toggle) {
      if (this.classList.contains("is-busy")) return;

      const variantId = toggle.dataset.variantId;
      if (!variantId) return;

      const isSelected = toggle.dataset.selected === "true";

      if (!isSelected) {
        // Guard — unselected buttons are disabled at max, but re-check anyway.
        if (this.selectedCount >= this.max) {
          this.showError(
            (window.cartStrings && window.cartStrings.samplesLimitError) ||
              `You can select up to ${this.max} samples.`,
          );
          return;
        }
        this.clearError();
        this.setLoading(toggle, true);
        this.mutate(this.addSample(variantId), toggle);
      } else {
        this.clearError();
        this.setLoading(toggle, true);
        this.mutate(this.removeSample(variantId), toggle);
      }
    }

    addSample(variantId) {
      const form = new FormData();
      form.append("id", variantId);
      form.append("quantity", 1);
      form.append("properties[_sample]", "true");
      form.append("sections", SAMPLE_SECTIONS.join(","));
      form.append("sections_url", window.location.pathname);

      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        body: form,
      }).then((r) => r.json());
    }

    removeSample(variantId) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: String(variantId),
          quantity: 0,
          sections: SAMPLE_SECTIONS,
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
    }

    mutate(promise, toggle) {
      this.setBusy(true);
      promise
        .then((state) => {
          if (state && state.status) {
            // Add failed (e.g. sold out) — nothing changed, just report it.
            this.showError(state.description || state.message || "");
            this.setBusy(false);
            this.setLoading(toggle, false);
            return;
          }
          // Success — the section re-render replaces this DOM, spinner included.
          this.refreshCart(state);
        })
        .catch((error) => {
          console.error("Sample selection failed:", error);
          this.setBusy(false);
          this.setLoading(toggle, false);
        });
    }

    refreshCart(state) {
      // Keep the panel open across the re-render of the cart-drawer section.
      window.__sampleDrawerReopen = true;

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && state && state.sections) {
        cartDrawer.renderContents(state);
      }

      // Let other cart subscribers (count bubble, gift module) re-evaluate.
      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "sample-drawer",
          cartData: state,
        });
      }
    }
  }

  if (typeof customElements.get("sample-drawer") === "undefined") {
    customElements.define("sample-drawer", SampleDrawer);
  }

  // When the cart drawer closes (backdrop click, Esc, or Close button), also
  // dismiss the sample panel so reopening the cart shows the cart, not samples.
  const wireSampleDrawerToCartClose = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || cartDrawer.dataset.sampleCloseWired === "true") return;
    cartDrawer.dataset.sampleCloseWired = "true";

    new MutationObserver(() => {
      if (cartDrawer.classList.contains("active")) return;
      const sampleDrawer = document.querySelector("sample-drawer.is-open");
      if (sampleDrawer) sampleDrawer.classList.remove("is-open");
    }).observe(cartDrawer, { attributes: true, attributeFilter: ["class"] });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", wireSampleDrawerToCartClose);
  } else {
    wireSampleDrawerToCartClose();
  }
}
